import { trackAppInsightsTelemetry, TRACK_METHOD } from "~/logging/applicationInsightsService";
import { userManager } from "../userManager";
import { getCookie } from "~/helpers/cookies";
import { getConfig } from "~/config";

const getApiEndpoint = () => {
  const { apiBaseUrl } = getConfig();
  if (apiBaseUrl !== "") {
    // If the environment configures the API base URL, we use it. Otherwise we base the API endpoint on our
    // endpoint using replace rules
    return apiBaseUrl;
  }

  if (window.location.hostname === "dev-test1me.viedoc.net") {
    // QUICKFIX: The original test environment doesn't follow any naming standard.
    return "https://v4me-dev2.viedoc.net";
  } 
  
  const re = new RegExp(import.meta.env.VITE_API_BASE_URL_FIND);
  const apiHostName = window.location.hostname.replace(
    re,
    import.meta.env.VITE_API_BASE_URL_REPLACE
  );
  return `${window.location.protocol}//${apiHostName}`;
};

const apiEndpoint = getApiEndpoint();

const STATUS_CODE = {
  NO_CONTENT: 204,
  ABORTED: 299, // There is no standard code for aborted
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
};

const CONTENT_TYPE = {
  JSON: "application/json"
};

const handleJsonResponse = (response) => {
  if (!response.ok) {
    return Promise.reject(response);
  }

  if (response.status === STATUS_CODE.NO_CONTENT) {
    return Promise.resolve(response);
  }

  return response.json();
};

// The API reports upload failures as a 400 whose body is the user-facing text,
// plain or JSON; only that body is kept, since a 502 or 504 from the reverse
// proxy is an HTML error page. The transport status wins over a `status` in a
// problem-details body, as the global SWR error handler signs out on 401.
const toRequestError = (status, statusText, rawBody) => {
  if (status !== STATUS_CODE.BAD_REQUEST || !rawBody) {
    return { status, statusText };
  }

  const result = tryJsonParse(rawBody);
  const body = typeof result === "string" ? { message: result } : result;

  return { ...body, status, statusText };
};

// Same as handleJsonResponse, but a failed response rejects with the parsed
// error body instead of the raw Response, so upload callers get the backend's
// message on the fetch-based path too, not only from postWithProgress.
const handleJsonResponseWithErrorBody = async (response) => {
  if (response.ok) {
    return handleJsonResponse(response);
  }

  const rawBody = await response.text();
  throw toRequestError(response.status, response.statusText, rawBody);
};

const handleBlobResponse = (response) => {
  if (!response.ok) {
    return Promise.reject(response);
  }

  return response.blob();
};

async function getLocalToken() {
  const user = await userManager.getUser();
  if (user) {
    return user.access_token;
  }

  // we already change the way to store access token from cookie to local storage
  // but worker generate pdf still use this cookie to generate so it's kind of fallback token for worker
  return getCookie(import.meta.env.VITE_TOKEN_COOKIE_NAME);
}

const getAuthHeader = async () => {
  const token = await getLocalToken();

  return {
    Authorization: `Bearer ${token}`,
  };
};

const tryJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch (_) {
    return str;
  }
};

const validateUrl = (url) => {
  if (url[0] !== "/") {
    throw Error("URL should start with '/'");
  }
};

const getHeaders = async () => {
  const authHeader = await getAuthHeader();

  return {
    "Content-Type": CONTENT_TYPE.JSON,
    ...authHeader,
  };
};

const baseRequest = async ({
  url,
  method,
  body,
  onSuccess = handleJsonResponse,
}) => {
  validateUrl(url);

  const headers = await getHeaders();

  return fetch(`${apiEndpoint}${url}`, {
    method,
    body: body ? JSON.stringify(body) : null,
    headers,
  }).then(onSuccess);
};

const get = async (url) => {
  return baseRequest({ url, method: "GET" });
};

const getFile = async (url) => {
  return baseRequest({ url, method: "GET", onSuccess: handleBlobResponse });
};

const post = async (url, { arg: body } = {}) => {
  return baseRequest({
    url,
    method: "POST",
    body,
  });
};

// POST that rejects with `{ status, statusText, message? }` like
// postWithProgress does, for endpoints whose 400 body is the user-facing text.
const postWithErrorBody = async (url, { arg: body } = {}) => {
  return baseRequest({
    url,
    method: "POST",
    body,
    onSuccess: handleJsonResponseWithErrorBody,
  });
};

// Since fetch() does not support progress event on POST,
// use XMLHttpRequest instead
const postWithProgress = async (
  url,
  formData,
  progressHandler,
  abortCallback
) => {
  let headers = {};
  try {
    headers = await getAuthHeader(true);
  } catch (error) {
    trackAppInsightsTelemetry(error, TRACK_METHOD.EXCEPTION);
    throw error;
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let abortPolling = null;

    const clearAbortPolling = () => {
      if (abortPolling) {
        clearInterval(abortPolling);
        abortPolling = null;
      }
    };

    // Poll initiator for request abort
    if (abortCallback) {
      abortPolling = setInterval(() => {
        if (abortCallback()) {
          clearAbortPolling();
          xhr.abort();
        }
      }, 200);
    }

    xhr.open("POST", `${apiEndpoint}${url}`);

    // Transfer complete
    xhr.onload = function () {
      clearAbortPolling();
      if (this.status >= 200 && this.status < 300) {
        resolve(xhr.response ? tryJsonParse(xhr.response) : null);
      } else {
        reject(toRequestError(this.status, xhr.statusText, xhr.response));
      }
    };

    // Transfer failed
    xhr.onerror = function () {
      clearAbortPolling();
      reject({
        status: this.status,
        statusText: xhr.statusText,
      });
    };

    // Transfer progress update
    xhr.upload.onprogress = (event) => {
      progressHandler(event);
    };

    // Transfer canceled
    xhr.onabort = () => {
      clearAbortPolling();
      reject({
        status: STATUS_CODE.ABORTED,
        statusText: xhr.statusText,
      });
    };

    for (const key in headers) {
      xhr.setRequestHeader(key, headers[key]);
    }

    xhr.send(formData);
  });
};

const put = async (url, { arg: body }) => {
  return baseRequest({ url, method: "PUT", body });
};

export const http = {
  get,
  getFile,
  post,
  postWithErrorBody,
  postWithProgress,
  put,
  STATUS_CODE,
};
